import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const opt = "w-full text-left rounded-xl border px-4 py-3 transition-colors duration-200 disabled:opacity-60";

export default function DeleteCardButton({ card, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const remove = async (stop) => {
    setBusy(true);
    try {
      await api.delete(`/cards/${card.id}`, { params: { stop_job: stop } });
      toast.success(stop ? "Job card deleted and recurring job stopped" : "Job card deleted for this period");
      onDeleted();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid="job-card-delete-button"
          className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-4 h-9 text-sm font-medium text-red-600 hover:bg-red-600 hover:text-white transition-colors duration-200"><Trash2 size={13} /> Delete</button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-2xl bg-[#FDFBF7]" data-testid="job-card-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif-brand text-2xl text-[#103080]">Delete this job card?</AlertDialogTitle>
          <AlertDialogDescription>{card.client_name} · {card.title} · {card.period_label}. Its tasks come off the planner. Time already logged stays on timesheets.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <button disabled={busy} onClick={() => remove(false)} data-testid="job-card-delete-this" className={`${opt} border-[#103080]/15 bg-white hover:border-red-300`}>
            <p className="font-semibold text-[#103080] text-sm">Delete just this period</p>
            <p className="text-xs text-[#0F172A]/55">Future job cards for this client and service still come through.</p>
          </button>
          {card.job_id && (
            <button disabled={busy} onClick={() => remove(true)} data-testid="job-card-delete-stop" className={`${opt} border-red-200 bg-red-50/60 hover:border-red-400`}>
              <p className="font-semibold text-red-700 text-sm">Delete this card and stop the recurring job</p>
              <p className="text-xs text-[#0F172A]/55">No more cards for this service. Other open cards for this job are removed too; the client stays.</p>
            </button>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid="job-card-delete-cancel">Cancel</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
